import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Landmark, Plus } from "lucide-react";
import api, { apiError, ruleReasons } from "../../lib/api";
import { BANK_STATUS, fmtDate, fmtDateTime, fmtMoney, INVOICE_STATUS, PO_STATUS, QUOTE_STATUS, SUPPLIER_STATUS } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ReasonsList, ToneChip } from "../../components/common";
import { FinActions, Money } from "../../components/finance";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

function NewSupplierDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", tax_id: "", category: "Servicios", contact_email: "", iban: "", supporting_document: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/finanzas/suppliers", form);
      toast.success(`Proveedor ${r.data.code} dado de alta — pendiente de validación`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-supplier-dialog">
        <DialogHeader><DialogTitle>Alta de proveedor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>NIF/CIF</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} data-testid="supplier-tax" /></div>
            <div><Label>Categoría</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="supplier-category" /></div>
          </div>
          <div><Label>Email de contacto</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="supplier-email" /></div>
          <div><Label>IBAN inicial</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="ES…" data-testid="supplier-iban" /></div>
          <div><Label>Documento de soporte (certificado bancario)</Label><Input value={form.supporting_document} onChange={(e) => setForm({ ...form, supporting_document: e.target.value })} data-testid="supplier-doc" /></div>
          <p className="text-xs text-slate-500">La cuenta queda pendiente de verificación; sin cuenta verificada no hay pagos.</p>
          <Button className="w-full" onClick={submit} disabled={busy || !form.name.trim()} data-testid="supplier-submit">{busy ? "Creando…" : "Dar de alta"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BankChangeDialog({ supplier, open, onClose, onDone }) {
  const [form, setForm] = useState({ iban: "", bank_name: "", change_reason: "", supporting_document: "", source: "email" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/finanzas/suppliers/${supplier.id}/bank-change`, form);
      toast.warning("Cambio registrado: pagos bloqueados hasta verificación fuera de banda por persona distinta (maker-checker)");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="bank-change-dialog">
        <DialogHeader><DialogTitle>Registrar cambio de cuenta — {supplier?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="rounded-sm border border-red-200 bg-red-50 p-2 text-xs text-red-800">
            Control antifraude: la cuenta anterior se conserva, los pagos se bloquean y la verificación exige contacto fuera de banda (teléfono conocido, no el del email) realizado por otra persona.
          </p>
          <div><Label>Nuevo IBAN *</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="bank-iban" /></div>
          <div><Label>Banco</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="bank-name" /></div>
          <div>
            <Label>Origen de la solicitud</Label>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger data-testid="bank-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="carta">Carta</SelectItem>
                <SelectItem value="portal">Portal de proveedor</SelectItem>
                <SelectItem value="llamada">Llamada entrante</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Motivo declarado</Label><Textarea value={form.change_reason} onChange={(e) => setForm({ ...form, change_reason: e.target.value })} data-testid="bank-reason" /></div>
          <div><Label>Documento de soporte *</Label><Input value={form.supporting_document} onChange={(e) => setForm({ ...form, supporting_document: e.target.value })} data-testid="bank-doc" /></div>
          <Button className="w-full" onClick={submit} disabled={busy || form.iban.trim().length < 10 || !form.supporting_document.trim()} data-testid="bank-submit">
            {busy ? "Registrando…" : "Registrar cambio (bloquea pagos)"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VerifyDialog({ account, open, onClose, onDone }) {
  const [oob, setOob] = useState({ contacted_person: "", phone_used: "", phone_origin: "", comment: "" });
  const [busy, setBusy] = useState(false);
  const [reasons, setReasons] = useState(null);
  const submit = async () => {
    setBusy(true);
    setReasons(null);
    try {
      await api.post(`/finanzas/bank-accounts/${account.id}/verify`, { out_of_band: oob });
      toast.success("Cuenta verificada fuera de banda — pagos desbloqueados");
      onDone();
    } catch (e) {
      const r = ruleReasons(e);
      if (r) setReasons(r);
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="verify-account-dialog">
        <DialogHeader><DialogTitle>Verificación fuera de banda — ····{account?.iban?.slice(-4)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {reasons && <ReasonsList reasons={reasons} testid="oob-blocked-reasons" />}
          <p className="text-xs text-slate-600">Un email no es suficiente. Registra la llamada al contacto conocido usando el teléfono del contrato o ficha validada. Quien registró el cambio no puede verificarlo.</p>
          <div><Label>Persona contactada *</Label><Input value={oob.contacted_person} onChange={(e) => setOob({ ...oob, contacted_person: e.target.value })} data-testid="oob-person" /></div>
          <div><Label>Teléfono utilizado *</Label><Input value={oob.phone_used} onChange={(e) => setOob({ ...oob, phone_used: e.target.value })} data-testid="oob-phone" /></div>
          <div>
            <Label>Origen del teléfono *</Label>
            <Select value={oob.phone_origin} onValueChange={(v) => setOob({ ...oob, phone_origin: v })}>
              <SelectTrigger data-testid="oob-origin"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="contrato">Contrato vigente</SelectItem>
                <SelectItem value="ficha_validada">Ficha de proveedor validada</SelectItem>
                <SelectItem value="registro_mercantil">Registro mercantil</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Comentario</Label><Textarea value={oob.comment} onChange={(e) => setOob({ ...oob, comment: e.target.value })} data-testid="oob-comment" /></div>
          <Button className="w-full" onClick={submit} disabled={busy || !oob.contacted_person.trim() || !oob.phone_used.trim() || !oob.phone_origin} data-testid="oob-submit">
            {busy ? "Verificando…" : "Confirmar verificación"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SupplierDetail({ sid, onClose }) {
  const { map } = useUsers();
  const [bankOpen, setBankOpen] = useState(false);
  const [verify, setVerify] = useState(null);
  const { data, refetch } = useQuery({
    queryKey: ["fin-supplier", sid],
    queryFn: async () => (await api.get(`/finanzas/suppliers/${sid}`)).data,
  });
  if (!data) return null;
  const { supplier: s, bank_accounts, quotes, purchase_orders, invoices, exceptions, actions } = data;

  const act = async (action, payload) => {
    await api.post(`/finanzas/suppliers/${s.id}/action`, { action, ...payload });
    toast.success("Acción registrada");
    refetch();
  };

  return (
    <div className="mt-4 space-y-4" data-testid={`supplier-detail-${s.id}`}>
      <div className="ts-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-display text-lg font-semibold">{s.name}</p>
              <Chip map={SUPPLIER_STATUS} value={s.status} testid="supplier-detail-status" />
            </div>
            <p className="mt-1 text-xs text-slate-500">{s.code} · NIF {s.tax_id || "—"} · {s.category} · {s.payment_terms} · alta {fmtDate(s.created_at)}</p>
            <p className="text-xs text-slate-500">Contacto: {s.contact_name || "—"} ({s.contact_email || "—"}) · responsable interno: {userName(map, s.owner_id)}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="supplier-close">Cerrar</Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <FinActions actions={actions} onAct={act} testid="supplier-action" />
          <Button size="sm" variant="outline" onClick={() => setBankOpen(true)} data-testid="bank-change-button">
            <Landmark className="mr-1 h-3.5 w-3.5" /> Registrar cambio de cuenta
          </Button>
        </div>
      </div>

      <div className="ts-card overflow-hidden" data-testid="supplier-accounts">
        <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Cuentas bancarias</p>
        <Table>
          <TableHeader><TableRow><TableHead>IBAN</TableHead><TableHead>Banco</TableHead><TableHead>Origen</TableHead><TableHead>Estado</TableHead><TableHead>Verificación</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {bank_accounts.map((a) => (
              <TableRow key={a.id} data-testid={`account-${a.id}`} className={a.status === "bloqueada" ? "bg-red-50/50" : ""}>
                <TableCell className="font-mono text-xs">{a.iban}</TableCell>
                <TableCell className="text-xs">{a.bank_name || "—"}</TableCell>
                <TableCell className="text-xs">{a.source}</TableCell>
                <TableCell><Chip map={BANK_STATUS} value={a.status} testid={`account-status-${a.id}`} /></TableCell>
                <TableCell className="text-xs text-slate-500">
                  {a.verified_by ? `${userName(map, a.verified_by)} · ${fmtDateTime(a.verified_at)} (${a.verification_method})` : "—"}
                </TableCell>
                <TableCell className="text-right">
                  {["bloqueada", "pendiente_verificacion", "pendiente"].includes(a.status) && (
                    <Button size="sm" variant="outline" onClick={() => setVerify(a)} data-testid={`verify-account-${a.id}`}>Verificar</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="ts-card overflow-hidden" data-testid="supplier-invoices">
          <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Facturas ({invoices.length})</p>
          <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
            {invoices.map((i) => (
              <div key={i.id} className="flex items-center justify-between px-5 py-2 text-xs">
                <span className="font-mono">{i.invoice_number}</span>
                <Chip map={INVOICE_STATUS} value={i.status} />
                <Money value={i.total} />
              </div>
            ))}
            {!invoices.length && <p className="px-5 py-4 text-center text-xs text-slate-400">Sin facturas</p>}
          </div>
        </div>
        <div className="ts-card overflow-hidden" data-testid="supplier-pos">
          <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Órdenes ({purchase_orders.length})</p>
          <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
            {purchase_orders.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-2 text-xs">
                <span className="font-mono">{p.po_number}</span>
                <Chip map={PO_STATUS} value={p.status} />
                <Money value={p.total} />
              </div>
            ))}
            {!purchase_orders.length && <p className="px-5 py-4 text-center text-xs text-slate-400">Sin órdenes</p>}
          </div>
        </div>
        <div className="ts-card overflow-hidden" data-testid="supplier-quotes">
          <p className="border-b border-slate-100 px-5 py-3 font-display text-sm font-semibold">Ofertas ({quotes.length})</p>
          <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
            {quotes.map((q) => (
              <div key={q.id} className="flex items-center justify-between px-5 py-2 text-xs">
                <span className="font-mono">{q.quote_number}</span>
                <Chip map={QUOTE_STATUS} value={q.status} />
                <Money value={q.total} />
              </div>
            ))}
            {!quotes.length && <p className="px-5 py-4 text-center text-xs text-slate-400">Sin ofertas</p>}
          </div>
        </div>
      </div>

      {exceptions.length > 0 && (
        <div className="ts-card p-5" data-testid="supplier-exceptions">
          <p className="mb-2 font-display text-sm font-semibold">Excepciones del proveedor</p>
          {exceptions.map((e) => (
            <div key={e.id} className="mb-2 rounded-sm border border-red-200 bg-red-50 p-2 text-xs" data-testid={`supplier-exc-${e.id}`}>
              <span className="font-semibold text-red-800">{e.type}</span> — {e.description} <ToneChip label={e.status} tone={e.status === "resuelta" ? "success" : "danger"} />
            </div>
          ))}
        </div>
      )}

      <BankChangeDialog supplier={s} open={bankOpen} onClose={() => setBankOpen(false)} onDone={() => { setBankOpen(false); refetch(); }} />
      {verify && <VerifyDialog account={verify} open onClose={() => setVerify(null)} onDone={() => { setVerify(null); refetch(); }} />}
    </div>
  );
}

export default function Proveedores() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { data: items, refetch } = useQuery({
    queryKey: ["fin-suppliers", status, q],
    queryFn: async () => (await api.get("/finanzas/suppliers", { params: { status: status || undefined, q: q || undefined } })).data,
  });
  const selected = params.get("supplier");

  return (
    <div data-testid="proveedores-page">
      <PageHeader
        title="Proveedores"
        subtitle="Alta validada, cuentas bancarias verificadas y control antifraude de cambios de IBAN."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-supplier-button"><Plus className="mr-1 h-4 w-4" /> Alta de proveedor</Button>}
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Buscar por nombre, código, NIF…" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" data-testid="supplier-search" />
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="supplier-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(SUPPLIER_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items && items.length === 0 && <EmptyState title="Sin proveedores" description="Da de alta el primer proveedor." />}
      {items?.length > 0 && (
        <div className="ts-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Proveedor</TableHead><TableHead>Categoría</TableHead><TableHead>NIF</TableHead><TableHead>Condiciones</TableHead><TableHead>Estado</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`} className={`cursor-pointer ${selected === s.id ? "bg-blue-50/60" : ""}`} onClick={() => setParams(selected === s.id ? {} : { supplier: s.id })}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{s.name}</p>
                    <p className="font-mono text-xs text-slate-400">{s.code}</p>
                  </TableCell>
                  <TableCell className="text-xs">{s.category}</TableCell>
                  <TableCell className="font-mono text-xs">{s.tax_id || "—"}</TableCell>
                  <TableCell className="text-xs">{s.payment_terms}</TableCell>
                  <TableCell><Chip map={SUPPLIER_STATUS} value={s.status} testid={`supplier-status-${s.id}`} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {selected && <SupplierDetail sid={selected} onClose={() => setParams({})} />}
      <NewSupplierDialog open={open} onClose={() => setOpen(false)} onDone={() => { setOpen(false); refetch(); qc.invalidateQueries({ queryKey: ["fin-supplier"] }); }} />
    </div>
  );
}
